import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, MessageCircle, AlertCircle, Scale, Users, ChevronRight, DollarSign, CheckCircle, ChevronDown, Menu, X } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import PageVideoSection from '@/components/PageVideoSection';

// FAQ-related videos
const faqVideos = [
  {
    id: 'lk4IqFkRkG0',
    title: "Busting Personal Injury Myths",
    description: "Common myths about claims and timelines, debunked by an attorney.",
    duration: "3:27"
  },
  {
    id: 'DRckbwMdDKE',
    title: "Medical Bills After an Accident",
    description: "Who pays and how to handle co-pays during your claim.",
    duration: "1:10"
  }
];

const FAQPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('washington-alaska');
  const [openItem, setOpenItem] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const contentRef = useRef(null);

  // Navigation sections
  const navSections = [
    { id: 'washington-alaska', label: 'Washington & Alaska Law', icon: '⚖️' },
    { id: 'injury-basics', label: 'Personal Injury Basics', icon: '📋' },
    { id: 'working-with-amy', label: 'Working with Amy', icon: '🤝' },
    { id: 'car-accidents', label: 'Car Accidents', icon: '🚗' },
    { id: 'maritime', label: 'Maritime Injuries', icon: '⚓' },
    { id: 'first-responders', label: 'First Responders', icon: '🚔' },
    { id: 'problems-risks', label: 'Problems & Risks', icon: '⚠️' },
    { id: 'comparisons', label: 'Comparisons', icon: '⚖️' },
    { id: 'best-practices', label: 'Best Practices', icon: '✓' },
  ];

  // Handle deep linking from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && navSections.some(s => s.id === hash)) {
      setActiveSection(hash);
      setOpenItem(null);
      // Scroll to content on mobile
      if (window.innerWidth < 1024) {
        setTimeout(() => {
          contentRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  // FAQ data organized by section
  const faqSections = {
    'washington-alaska': {
      title: 'Washington & Alaska Law',
      description: 'State-specific legal information for injury claims',
      faqs: [
        {
          question: 'What is the statute of limitations for personal injury in Washington State?',
          answer: 'In most personal injury cases in Washington — car accidents, motorcycle crashes, slip and falls, wrongful death — you generally have three years from the date of the injury to file a lawsuit (RCW 4.16.080). For wrongful death, it\'s three years from the date of death. Some exceptions exist, such as cases involving minors, delayed discovery of the injury, or claims against government entities (which can be much shorter, sometimes 60–90 days for notice). Because evidence disappears and memories fade quickly, I always recommend reaching out as soon as possible after an accident or loss.'
        },
        {
          question: 'How long do I have to file a Jones Act claim in Washington or Alaska?',
          answer: 'For Jones Act and most maritime injury claims (including those involving Washington or Alaska waters), you generally have three years from the date of the injury to file a lawsuit (46 U.S.C. § 30106). This applies to negligence claims against your employer or unseaworthiness claims against the vessel owner. Maintenance and cure benefits don\'t have the same strict deadline, but you should claim them promptly — delays can complicate proof and payment.'
        },
        {
          question: 'Can I sue if I was injured on a fishing boat in Alaska but live in Washington?',
          answer: 'Yes — if you were injured while working as a seaman on a fishing boat in Alaska (or anywhere in navigable waters), you likely have strong rights under federal maritime law, including the Jones Act, no matter where you live. The Jones Act applies nationwide to U.S. seamen and allows you to sue your employer for negligence if unsafe conditions contributed to your injury — even slightly. You\'re also entitled to maintenance and cure regardless of fault.'
        }
      ]
    },
    'injury-basics': {
      title: 'Personal Injury Basics',
      description: 'Understanding your rights after an injury',
      faqs: [
        {
          question: 'What compensation can I receive for my injuries?',
          answer: 'Personal injury law enables individuals injured by another\'s negligence to seek compensation for damages including medical expenses (current and future), lost wages and loss of earning capacity, pain and suffering, and property damage. The specific amount depends on your case circumstances. Washington law (RCW Chapter 4.22) governs comparative fault in personal injury cases.'
        },
        {
          question: 'Do I really need an attorney for my injury case?',
          answer: 'Hiring an experienced personal injury attorney can significantly increase your settlement. Studies from the Insurance Research Council show that represented claimants receive 3.5 times more on average—even after attorney fees—compared to those handling claims alone. Insurance companies have teams of lawyers; you deserve experienced representation too.'
        },
        {
          question: 'What should I do immediately after an accident?',
          answer: 'First, seek medical attention — even if you feel okay, adrenaline can mask pain. Document everything: photos of the scene, injuries, and damage. Get contact information from witnesses. File a police report if applicable. Do not sign anything from insurance companies or give recorded statements without speaking to an attorney first. Contact me as soon as possible so I can help preserve evidence and protect your rights.'
        }
      ]
    },
    'working-with-amy': {
      title: 'Working with Amy C. Brown Law',
      description: 'What to expect when you hire me',
      faqs: [
        {
          question: 'How does your contingency fee basis work?',
          answer: 'I operate on a contingency fee basis, meaning you do not pay upfront legal fees. My compensation is contingent upon securing a successful outcome for your case—only when you win do I receive a percentage of the settlement or award. This approach alleviates financial pressure during an already challenging time, allowing you to focus on recovery rather than costs.'
        },
        {
          question: 'Will I work directly with you, or with paralegals?',
          answer: 'As a solo practitioner, I offer direct, one-on-one communication. You will work directly with me throughout your entire case. I answer every call and email myself to keep clients informed and supported, eliminating the frustration of navigating large firm hierarchies.'
        },
        {
          question: 'How often will you update me on my case?',
          answer: 'One of the most common frustrations clients express about lawyers is the lack of regular updates. I am deeply committed to ensuring that every client I represent is kept fully informed every step of the way, providing clear and timely communication throughout your case.'
        },
        {
          question: 'What is your experience level?',
          answer: 'I have over ten years of legal experience. After graduating with honors from Seattle University School of Law, where I served as an intern for a federal judge in the Western District of Washington, I worked at a law firm representing prominent national clients. This experience provided me with valuable insight into insurance company strategies.'
        },
        {
          question: 'Why does your defense background help me?',
          answer: 'Having started my career defending employers, shipowners, and insurance companies, I gained a rare understanding of their strategies, which I now use to effectively represent plaintiffs. I know how insurance companies operate and how to counter their tactics, allowing me to anticipate their moves and fight for the full benefits you\'re entitled to.'
        }
      ]
    },
    'car-accidents': {
      title: 'Car Accidents',
      description: 'Questions about auto collision claims',
      faqs: [
        {
          question: 'What should I do after a car accident?',
          answer: 'After a car crash, seek medical attention first. Document everything—take photos, get witness information, and file a police report. Do not sign anything from the insurance company before speaking with an attorney. Insurance companies may pressure you to sign documents that limit your recovery.'
        },
        {
          question: 'Can I still recover damages if I had preexisting injuries?',
          answer: 'Yes. I won arbitration of a rear end collision against a major auto insurer with significant preexisting injury. Preexisting conditions don\'t bar recovery—defendants take victims as they find them. What matters is whether the accident aggravated or worsened your condition.'
        },
        {
          question: 'What if I was injured at work while driving?',
          answer: 'I successfully settled a claim for a worker injured on the job by a drunk driver, in which there were significant gaps in medical treatment. Even if you were working at the time, you may have claims beyond workers\' compensation against the at-fault driver.'
        }
      ]
    },
    'maritime': {
      title: 'Maritime Injuries',
      description: 'Jones Act and maritime law questions',
      faqs: [
        {
          question: 'What is the Jones Act?',
          answer: 'The Jones Act, formally known as the Merchant Marine Act of 1920 (46 U.S.C. § 30104), is a federal law designed specifically to protect seamen—workers like deckhands, fishermen, or offshore rig workers. It provides unique protections including the ability to sue your employer for negligence and claims for unseaworthiness.'
        },
        {
          question: 'What are maintenance and cure?',
          answer: 'Maintenance is a daily stipend for living expenses (food and lodging) that you\'re entitled to if you\'re injured while working on a vessel and cannot work. Cure refers to medical care—you must receive payment for your medical expenses until you reach maximum medical improvement. These benefits are unique to maritime law and owed regardless of fault.'
        },
        {
          question: 'How is the Jones Act different from regular injury law?',
          answer: 'Under the Jones Act, you only need to show that your employer\'s negligence contributed to your injury, even slightly, to recover compensation. This is a lower burden of proof than regular civil law. The Jones Act also allows claims for unseaworthiness, holding shipowners accountable for unsafe vessel conditions.'
        },
        {
          question: 'What types of maritime workers do you help?',
          answer: 'I help fishermen, deckhands, longshoremen, seamen, shipyard workers, harbor pilots, tugboat crew, offshore platform workers, commercial divers, cruise ship crew, maritime construction workers, ferry operators, maritime engineers, port workers, and marine cargo handlers.'
        }
      ]
    },
    'first-responders': {
      title: 'First Responder Cases',
      description: 'Police officers and emergency personnel',
      faqs: [
        {
          question: 'Can peace officers sue criminals for injuries from on-duty crashes?',
          answer: 'Yes. Peace officers often assume they cannot sue criminals for injuries sustained in work-related crashes, relying solely on workers\' compensation. This is not accurate. In Washington State, officers can pursue civil claims against at-fault parties, including criminals and their insurers, for damages beyond workers\' comp benefits. I have pursued these cases and won.'
        },
        {
          question: 'What additional compensation can first responders receive?',
          answer: 'Beyond workers\' compensation, first responders can pursue claims for pain and suffering, emotional distress, full wage replacement (workers\' comp often only covers 60-75%), and future medical needs that may emerge later. Lawsuits allow recovery for things like extended therapy or adaptive equipment not covered by workers\' comp.'
        },
        {
          question: 'Do you have experience with first responder cases?',
          answer: 'Yes. I obtained a six-figure settlement for an on-duty peace officer who sustained a surgical injury after lawful intervention in a pursuit. The officer was rammed by the suspect, and despite assumptions of workers\' comp exclusivity, I secured compensation from the criminal\'s motor vehicle insurer.'
        }
      ]
    },
    'problems-risks': {
      title: 'Problems & Risks',
      description: 'Common pitfalls to avoid',
      faqs: [
        {
          question: 'What if the insurance company denies my claim or offers a very low settlement?',
          answer: 'Denials and lowball offers are common tactics — insurers may claim pre-existing conditions, insufficient evidence, or that your injury isn\'t severe. Don\'t accept or sign anything without review. I fight denials with medical records, expert opinions, witness statements, and strong negotiation. Many "denied" or low offers turn into fair settlements once properly challenged.'
        },
        {
          question: 'What if I have a pre-existing condition or previous injury?',
          answer: 'A pre-existing condition does not automatically destroy your claim. If the accident aggravated or worsened it, you can recover for the aggravation — additional pain, lost wages, treatment needs. Insurers push this defense hard; I counter with medical records showing the before/after difference. Full honesty upfront protects you best.'
        },
        {
          question: 'What happens if I wait too long to get medical care or report the injury?',
          answer: 'Delays can seriously hurt your case. Insurers argue "if it was really bad, you\'d have sought help immediately," which weakens credibility and evidence. Washington has a 3-year statute of limitations, but waiting makes photos, witnesses, and records harder to obtain. Seek care right away and document everything.'
        }
      ]
    },
    'comparisons': {
      title: 'Comparisons',
      description: 'Understanding your options',
      faqs: [
        {
          question: 'Contingency fee vs. hourly billing — which is better for injury cases?',
          answer: 'Contingency means no upfront cost and shared risk — I only get paid if you win. Hourly billing can cost thousands regardless of outcome, which is risky if the case drags or doesn\'t succeed. For most injury clients (especially with medical bills piling up), contingency is far more accessible and fair.'
        },
        {
          question: 'Solo attorney vs. big law firm for personal injury or maritime cases?',
          answer: 'Big firms often pass your case to paralegals or junior staff — updates can be slow, and you rarely speak directly to the lead attorney. With me, you work one-on-one — I answer every call and email personally, know your case inside out, and fight harder because my reputation is on the line.'
        },
        {
          question: 'Jones Act vs. state workers\' comp for maritime injuries?',
          answer: 'Jones Act lets seamen sue employers for negligence (higher potential recovery, including pain/suffering). State workers\' comp is no-fault but capped, excludes pain damages, and limits long-term benefits. If you\'re a qualified seaman, Jones Act often provides more — but it\'s complex. I help determine which applies and maximize your options.'
        }
      ]
    },
    'best-practices': {
      title: 'Best Practices',
      description: 'Expert guidance after an injury',
      faqs: [
        {
          question: 'Best steps to take immediately after a car or motorcycle crash in Washington?',
          answer: '1) Get to safety and call 911 if anyone is injured. 2) Document everything: photos of vehicles, damage, road conditions, injuries. 3) Get contact info from other drivers and witnesses. 4) Seek medical care right away — even if you feel "okay." 5) Report briefly to your insurance — avoid detailed recorded statements until you speak with a lawyer. 6) Call an attorney before the other side\'s insurer contacts you.'
        },
        {
          question: 'What do past clients say about working with me?',
          answer: 'Clients appreciate direct access, clear communication, and fighting for maximum recovery without unnecessary delays or bureaucracy. Real results include six-figure settlements for first responders and workers injured on the job. See testimonials from clients who have been through the process and found success with compassionate, personal representation.'
        }
      ]
    }
  };

  // Collect all FAQs for structured data
  const allFaqsForSchema = Object.values(faqSections).flatMap(section => 
    section.faqs.map(faq => ({
      question: faq.question,
      answer: typeof faq.answer === 'string' ? faq.answer : faq.question
    }))
  );

  // Handle section click - updates URL hash for deep linking
  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    setOpenItem(null);
    setMobileNavOpen(false);
    
    // Update URL hash for deep linking
    navigate(`/faqs#${sectionId}`, { replace: true });
    
    // Smooth scroll to top of content on mobile
    if (window.innerWidth < 1024) {
      contentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Toggle FAQ item
  const toggleItem = (index) => {
    setOpenItem(openItem === index ? null : index);
  };

  const currentSection = faqSections[activeSection];

  return (
    <div className="min-h-screen pt-20" data-testid="faq-page">
      <SEOHead 
        title="FAQs - Personal Injury Questions Answered | Amy C. Brown Law"
        description="Get answers to common questions about personal injury cases, maritime law, contingency fees, and working with Amy C. Brown Law in Washington and Alaska."
        faqData={allFaqsForSchema.slice(0, 15)}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-12 md:pt-40 md:pb-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mb-4">
              You Ask, I Answer
            </h1>
            <p className="text-lg text-slate-600 mb-4">
              Transparent answers to real questions. I believe in providing honest, straightforward information to help you make informed decisions.
            </p>
            <p className="text-primary text-sm italic">
              This is general information — not personalized legal advice.
            </p>
          </div>
        </div>
      </section>

      {/* Sidebar + Content Layout */}
      <section className="bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col lg:flex-row min-h-[600px]">
            
            {/* Mobile Navigation Toggle */}
            <div className="lg:hidden py-4 border-b border-slate-200 bg-white sticky top-20 z-30">
              <div className="relative">
                <button
                  onClick={() => setMobileNavOpen(!mobileNavOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-slate-200 shadow-sm"
                >
                  <span className="font-medium text-slate-700">
                    FAQ Topics
                  </span>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mobile Nav Dropdown */}
                {mobileNavOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border border-slate-200 shadow-xl z-40 max-h-80 overflow-y-auto">
                    {navSections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => handleSectionClick(section.id)}
                        className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                          activeSection === section.id 
                            ? 'bg-teal-50 text-teal-700 font-medium' 
                            : 'hover:bg-slate-50 text-slate-700'
                        }`}
                      >
                        <span>{section.icon}</span>
                        {section.label}
                        {activeSection === section.id && <span className="ml-auto text-teal-600">✓</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Sidebar Navigation */}
            <aside className="hidden lg:block w-72 flex-shrink-0 py-8 pr-8">
              <nav className="sticky top-28 space-y-1">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 px-4">
                  FAQ Categories
                </h3>
                {navSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 flex items-center gap-3 ${
                      activeSection === section.id 
                        ? 'bg-[#4A9B9B] text-white shadow-md' 
                        : 'hover:bg-white hover:shadow-sm text-slate-700'
                    }`}
                  >
                    <span className="text-lg">{section.icon}</span>
                    <span className={`text-sm ${activeSection === section.id ? 'font-semibold' : 'font-medium'}`}>
                      {section.label}
                    </span>
                  </button>
                ))}
                
                {/* Pricing Link */}
                <div className="pt-4 mt-4 border-t border-slate-200">
                  <Link
                    to="/pricing"
                    className="w-full text-left px-4 py-3 rounded-lg bg-coral-50 text-primary hover:bg-coral-100 transition-colors flex items-center gap-3"
                  >
                    <DollarSign className="w-5 h-5" />
                    <span className="text-sm font-medium">Pricing & Costs</span>
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Link>
                </div>
              </nav>
            </aside>

            {/* Main Content Area */}
            <main ref={contentRef} className="flex-1 py-8 lg:py-12 lg:pl-8 lg:border-l border-slate-200">
              {/* Section Header */}
              <div className="mb-8">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-2">
                  {currentSection.title}
                </h2>
                <p className="text-slate-600">{currentSection.description}</p>
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {currentSection.faqs.map((faq, index) => (
                  <div 
                    key={index}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                    data-testid={`faq-item-${activeSection}-${index}`}
                  >
                    <button
                      onClick={() => toggleItem(index)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                    >
                      <span className="font-medium text-secondary pr-4">{faq.question}</span>
                      <ChevronDown 
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                          openItem === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    <div 
                      className={`overflow-hidden transition-all duration-300 ${
                        openItem === index ? 'max-h-[500px]' : 'max-h-0'
                      }`}
                    >
                      <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Section-specific CTAs */}
              {activeSection === 'comparisons' && (
                <div className="mt-8 text-center">
                  <Link 
                    to="/pricing"
                    className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-all"
                  >
                    <DollarSign className="w-5 h-5" />
                    View Full Pricing Details
                  </Link>
                </div>
              )}

              {activeSection === 'best-practices' && (
                <div className="mt-8 text-center">
                  <Link 
                    to="/#reviews"
                    className="inline-flex items-center gap-2 text-teal-600 font-medium hover:text-teal-700"
                  >
                    Read Client Testimonials
                    <ChevronRight className="w-5 h-5" />
                  </Link>
                </div>
              )}

              {/* Disclaimer */}
              <div className="mt-10 flex items-start gap-3 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">
                  <strong>Disclaimer:</strong> This is general information — not personalized legal advice. 
                  Contact me at <a href="tel:425-675-1224" className="text-teal-600 hover:underline">(425) 675-1224</a> to discuss your specific situation.
                </p>
              </div>
            </main>
          </div>
        </div>
      </section>

      {/* Video Resources Section */}
      <PageVideoSection
        videos={faqVideos}
        title="Helpful Videos"
        subtitle="Watch these videos to learn more about common questions and myths."
        theme="teal"
      />

      {/* CTA Section */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-4">
            Still Have Questions?
          </h2>
          <p className="text-lg text-teal-100 mb-8">
            I personally respond to every inquiry. Free consultation, no obligation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all shadow-lg"
              data-testid="faq-cta-call"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all"
            >
              <MessageCircle className="w-5 h-5" />
              Text (425) 675-1224
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
